/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import type { ShopperProducts } from '@/scapi';
import { ChevronRight } from 'lucide-react';
import { routes, routeHref } from '@/route-paths';
import { cn } from '@/lib/utils';

type PathRecord = Required<ShopperProducts.schemas['Category']>['parentCategoryTree'][0];

export default function CategoryBreadcrumbs({
    category,
    className,
    currentPageLabel,
}: {
    category: ShopperProducts.schemas['Category'];
    className?: string;
    /** Optional trailing, non-link crumb for the current page (e.g. a product name on the PDP) */
    currentPageLabel?: string;
}): ReactElement {
    const { t } = useTranslation('category');
    const items: PathRecord[] = category.parentCategoryTree ?? [{ id: category.id, name: category.name }];
    return (
        <nav
            aria-label={t('breadcrumbs.label')}
            className={cn(
                'category-breadcrumbs mb-6 pb-2 font-sans text-[13px] font-stjude-bold leading-5 tracking-normal normal-case',
                className
            )}
        >
            <ol className="flex flex-wrap items-center">
                <li key="home" className="flex items-center">
                    <Link to={routes.home} className="no-underline transition-colors hover:text-[#555555]">
                        {t('breadcrumbs.home')}
                    </Link>
                </li>
                {items.map((item) => (
                    <li key={item.id} className="flex items-center">
                        <ChevronRight className="mx-1 size-4 shrink-0" strokeWidth={1.7} />
                        <Link
                            to={routeHref(routes.category, { categoryId: item.id ?? '' })}
                            className="no-underline transition-colors hover:text-[#555555]">
                            {item.name}
                        </Link>
                    </li>
                ))}
                {currentPageLabel && (
                    <li key="current" className="flex items-center" aria-current="page">
                        <ChevronRight className="mx-1 size-4 shrink-0" strokeWidth={1.7} />
                        <span>{currentPageLabel}</span>
                    </li>
                )}
            </ol>
        </nav>
    );
}
